import { useState, useCallback, useRef } from 'react';
import { IoCloudUploadOutline } from 'react-icons/io5';
import './file-drop-zone.css';
import { formatFileSize } from '../../utils/helpers';
import BestStartButton from '../button';

const ACCEPTED_TYPES = {
  'application/pdf': '.pdf',
  'text/plain': '.txt',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
};

const ACCEPT_STRING = Object.values(ACCEPTED_TYPES).join(',');
const MAX_SIZE_BYTES = 2.5 * 1024 * 1024 * 1024; // 2.5 GB

export default function FileDropZone({ onFileSelected, onFilesSelected, disabled, multiple = false }) {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const validateFile = useCallback((file) => {
    if (!file) return 'No file selected.';
    if (!ACCEPTED_TYPES[file.type]) return 'File type not allowed. Accepted: PDF, TXT, DOC, DOCX.';
    if (file.size > MAX_SIZE_BYTES) return `File exceeds 2.5 GB limit (${formatFileSize(file.size)}).`;
    return null;
  }, []);

  const handleFiles = useCallback((fileList) => {
    const files = Array.from(fileList).filter(Boolean);
    if (files.length === 0) return;

    const valid = [];
    const errors = [];
    for (const file of files) {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
      } else {
        valid.push(file);
      }
    }

    setError(errors.length > 0 ? errors.join(' ') : null);

    if (valid.length > 0) {
      if (multiple && onFilesSelected) {
        onFilesSelected(valid);
      } else {
        valid.forEach((f) => onFileSelected?.(f));
      }
    }
  }, [validateFile, onFileSelected, onFilesSelected, multiple]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (disabled) return;
    const files = e.dataTransfer?.files;
    if (files?.length) handleFiles(files);
  }, [disabled, handleFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragOver(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleInputChange = useCallback((e) => {
    const files = e.target.files;
    if (files?.length) handleFiles(files);
    if (inputRef.current) inputRef.current.value = '';
  }, [handleFiles]);

  const handleClick = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  return (
    <div className="file-drop-zone-wrapper">
      <div
        className={`file-drop-zone ${dragOver ? 'file-drop-zone--active' : ''} ${disabled ? 'file-drop-zone--disabled' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Drop a file here or click to browse"
      >
        <span className="file-drop-zone-icon-ring">
          <IoCloudUploadOutline size={20} aria-hidden="true" />
        </span>
        <div className="file-drop-zone-content">
          <span className="file-drop-zone-text">
            {multiple ? 'Drag & drop files here' : 'Drag & drop a file here'}
          </span>
          <span className="file-drop-zone-hint">PDF, TXT, DOC, DOCX — max 2.5 GB</span>
        </div>
        <BestStartButton text="Browse files" width={120} height={32} />
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_STRING}
          onChange={handleInputChange}
          className="file-drop-zone-input"
          tabIndex={-1}
          aria-hidden="true"
          multiple={multiple}
        />
      </div>
      {error && <p className="file-drop-zone-error" role="alert">{error}</p>}
    </div>
  );
}
